// =====================
// Model Imports
// =====================
import {
  DepartmentModel,
  DepartmentResponseModel,
} from '../../core/models/department.model';
import {
  EmployeeModel,
  EmployeeResponseModel,
  EmployeeSummaryModel,
  EmployeeSummaryResponseModel,
} from '../../core/models/employee.model';
import {
  UserModel,
  UserResponseModel,
  UserResponsibilityModel,
  UserResponsibilityResponseModel,
} from '../../core/models/user.model';

// =====================
// User Parsers
// =====================

/**
 * Maps a backend user record to the application model, including its role and company identifiers.
 * When responsibilities are supplied, each department is parsed into its application model.
 * @param user Backend user response to map.
 * @returns The mapped user, or `null` when no user response is supplied.
 */
export function parseUser(user: UserResponseModel): UserModel {
  if (!user) {
    return null;
  }
  return {
    companyName: user.company_name,
    email: user.email,
    firstName: user.first_name,
    lastName: user.last_name,
    idCompany: user.id_company,
    idUser: user.id_user,
    idRole: user.id_role,
    idRoleOnOff: user.id_role_onoff,
    roleName: user.role_name,
    roleOnOffName: user.role_onoff_name,
    passwordChanged: user.password_changed,
    responsibilities: user.responsibilities?.map(resp => parseDepartment(resp)),
  };
}

/**
 * Maps a department lookup record to the application model's identifier and name.
 * @param department Backend department response to map.
 * @returns The mapped department, or `null` when no response is supplied.
 */
export function parseDepartment(
  department: DepartmentResponseModel
): DepartmentModel {
  if (!department) {
    return null;
  }
  return {
    idDepartmentOnoff: department.id_department_onoff,
    name: department.name,
  };
}

/**
 * Maps a user's company and role details together with parsed department responsibilities and
 * the backend-provided responsibility names.
 * @param user Backend user-responsibility response to map.
 * @returns The mapped user responsibilities, or `null` when no response is supplied.
 */
export function parseUserResponsibilityModel(
  user: UserResponsibilityResponseModel
): UserResponsibilityModel {
  if (!user) {
    return null;
  }
  return {
    idUser: user.id_user,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    idCompany: user.id_company,
    companyName: user.company_name,
    idRoleOnOff: user.id_role_onoff,
    roleOnOffName: user.role_onoff_name,
    responsibilities: user.responsibilities.map(resp => parseDepartment(resp)),
    responsibilityNames: user.responsibility_names,
  };
}

/**
 * Maps the employee list summary, normalizing contract start and probation end dates while
 * retaining the company, position, functional group, and status details.
 * @param employee Backend employee summary response to map.
 * @returns The mapped employee summary, or `null` when no response is supplied.
 */
export function parseEmployeeSummary(
  employee: EmployeeSummaryResponseModel
): EmployeeSummaryModel {
  if (!employee) {
    return null;
  }
  return {
    idEmployee: employee.id_employee,
    firstName: employee.first_name,
    lastName: employee.last_name,
    companyName: employee.company_name,
    contractStartDate: toDateOrNull(employee.contract_start_date),
    probationEndDate: toDateOrNull(employee.probation_end_date),
    stage: employee.stage,
    statusDescription: employee.status_description,
    functionalGroupName: employee.functional_group_name,
    idFunctionalGroup: employee.id_functional_group,
    idPosition: employee.id_position,
    positionName: employee.position_name,
  };
}

/**
 * Normalizes a backend date value for use in the application model.
 * Existing `Date` instances are returned unchanged; strings are parsed with the JavaScript `Date`
 * constructor.
 * @param value Backend date value as a `Date` or date-like string.
 * @returns The existing date or a `Date` parsed from the string.
 */
function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Normalizes a nullable backend date without converting an absent value.
 * @param value Backend date value as a `Date`, date-like string, or `null`.
 * @returns The normalized date, or `null` when the backend value is `null`.
 */
function toDateOrNull(value: Date | string | null): Date | null {
  if (value === null) {
    return null;
  }
  return toDate(value);
}

/**
 * Maps a full employee record to the application model and converts contract start and probation
 * end values to `Date` instances.
 * @param employee Backend employee response to map.
 * @returns The mapped employee, or `null` when no employee response is supplied.
 */
export function parseEmployee(employee: EmployeeResponseModel): EmployeeModel {
  if (!employee) {
    return null;
  }
  return {
    idEmployee: employee.id_employee,
    firstName: employee.first_name,
    lastName: employee.last_name,
    contractStartDate: toDate(employee.contract_start_date),
    probationEndDate: toDate(employee.probation_end_date),
    stage: employee.stage,
    statusDescription: employee.status_description,
    companyName: employee.company_name,
    functionalGroupName: employee.functional_group_name,
    idFunctionalGroup: employee.id_functional_group,
    idPosition: employee.id_position,
    positionName: employee.position_name,
    email: employee.email,
    mobileNumber: employee.mobile_number,
    sageId: employee.sage_id,
    telephoneNumber: employee.telephone_number,
  };
}
